import type { MediaData } from "@shared/lib/types/reports";

export type MediaHistoryDelta = {
  date: string;
  formattedDate: string;
  deltaViews: number;
  deltaLikes: number;
  deltaComments: number;
  deltaShares: number;
};

const toDateKey = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const getOrdinalSuffix = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${s[v % 10] || s[0]}`;
};

const formatHistoryDate = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${getOrdinalSuffix(day)} ${month}, ${hours}:${minutes}`;
};

/**
 * One media: keep first snapshot per date+minute, sort by time, then
 * compute non-negative deltas vs the previous snapshot.
 */
export function buildMediaHistoryDeltas(
  history: MediaData[] | undefined
): MediaHistoryDelta[] {
  if (!history?.length) return [];

  const firstByDate = new Map<string, MediaData>();
  for (const entry of history) {
    if (!entry?.createdAt) continue;
    const key = toDateKey(entry.createdAt);
    if (!firstByDate.has(key)) {
      firstByDate.set(key, entry);
    }
  }

  const sorted = [...firstByDate.values()].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const deltas: MediaHistoryDelta[] = [];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const prev = sorted[i - 1];

    deltas.push({
      date: current.createdAt,
      formattedDate: formatHistoryDate(current.createdAt),
      deltaViews: Math.max(0, (current.views || 0) - (prev.views || 0)),
      deltaLikes: Math.max(0, (current.likes || 0) - (prev.likes || 0)),
      deltaComments: Math.max(
        0,
        (current.comments || 0) - (prev.comments || 0)
      ),
      deltaShares: Math.max(0, (current.shares || 0) - (prev.shares || 0)),
    });
  }

  console.log(deltas);

  return deltas;
}
